import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { api, ApiError, type ContentPart, type ContentSegment, type PartBreakdown, type RequestContent, type RequestContentResponse } from "./api";
import { ErrorMessage, Loading } from "./components";
import { count, money, pct, tokens } from "./format";

/**
 * What a request actually sent to the model (TAI-2392), for the person who sent it only.
 * Everything here is shown as plain text: React escapes it, and nothing is rendered as markup.
 */

/** Fixed order and colour per part: colour follows the part, never its rank. */
const PART_COLOR: Record<ContentPart, string> = {
  tools: "var(--kp-series-1)",
  instructions: "var(--kp-series-2)",
  conversation: "var(--kp-series-3)",
  toolResults: "var(--kp-series-4)",
  injected: "var(--kp-series-5)",
  prompt: "var(--kp-series-6)",
};
const PART_HELP: Record<ContentPart, string> = {
  tools: "The list of tools the assistant can use, with every tool's instructions and parameters. Sent with every request.",
  instructions: "The assistant's system prompt and the project instructions it loaded at the start of the session.",
  conversation: "Earlier prompts and answers, resent so the model has the whole conversation.",
  toolResults: "Output of commands and files the assistant read, sent back to the model.",
  injected: "Reminders and context the assistant adds on its own as the session goes on.",
  prompt: "What was added this turn.",
};

const UNAVAILABLE: Record<string, string> = {
  disabled: "Viewing request content is not switched on here.",
  "no-pointer": "The gateway kept no full record for this request.",
  "not-found": "The full record for this request could not be found in the gateway's audit store.",
  ambiguous: "More than one record matches this request, so none is shown.",
  "too-large": "This request's record is too large to show here.",
};

const HELPER: Record<string, string> = {
  title: "Helper call: the assistant asked a small model to name the session. Not your prompt.",
  suggestion: "Helper call: the assistant asked for a suggested next prompt. Not your prompt.",
};

export function ContentView({ requestId }: { requestId: string }) {
  const q = useQuery({
    queryKey: ["content", requestId],
    queryFn: () => api.content(requestId),
    // Content is never kept once the view closes.
    gcTime: 0,
    staleTime: Number.POSITIVE_INFINITY,
    retry: false,
  });
  if (q.isPending) return <Loading />;
  if (q.error) {
    if (q.error instanceof ApiError && q.error.status === 404) {
      return <p className="forge-typography--body1">Only the person who made this request can see what was sent.</p>;
    }
    return <ErrorMessage error={q.error} />;
  }
  const c: RequestContentResponse = q.data;
  if (c.state === "unavailable") {
    return (
      <forge-inline-message>
        <forge-icon slot="icon" name="info" />
        <div slot="title">Nothing to show</div>
        <span>{UNAVAILABLE[c.reason] ?? "This request's content is not available."}</span>
      </forge-inline-message>
    );
  }
  return (
    <div className="stack gap-large content-view">
      {c.state === "cacheHit" && (
        <forge-inline-message theme="info-secondary">
          <forge-icon slot="icon" name="cached" />
          <div slot="title">Replayed from the response cache</div>
          <span>The gateway had answered this exact request before and sent the same response again. It was not billed.</span>
        </forge-inline-message>
      )}
      {c.helper && <p className="content-view__helper forge-typography--body1"><forge-icon name="smart_toy" /> {HELPER[c.helper]}</p>}
      <Breakdown c={c} />
      <ToolsSection c={c} />
      <MessagesSection c={c} />
      <ResponseSection c={c} />
      <p className="muted forge-typography--body1">
        Only you can see this. Each view is recorded in the app's activity log, without the content.
      </p>
    </div>
  );
}

// ------------------------------------------------------------------------ the breakdown

function Breakdown({ c }: { c: RequestContent }) {
  const b = c.breakdown;
  const parts = b.parts.filter((p) => p.tokens > 0);
  const billed = c.state !== "cacheHit" && b.inputCost > 0;
  return (
    <section className="stack gap-small">
      <p className="forge-typography--body1">
        <b>{tokens(b.inputTokens)} input tokens</b> went to the model
        {billed ? <>, for an estimated {money(b.inputCost)}. The answer was {tokens(b.outputTokens)} tokens ({money(b.outputCost)}).</> : "."}
      </p>
      <StackBar label="Input tokens" parts={parts} value={(p) => p.tokens} total={b.inputTokens}
        describe={(p) => `${count(p.tokens)} tokens · ${pct(p.tokens / Math.max(1, b.inputTokens))}`} />
      {billed && (
        <StackBar label="Estimated input cost" parts={parts} value={(p) => p.cost} total={b.inputCost}
          describe={(p) => `${money(p.cost)} · ${pct(p.cost / b.inputCost)}`} />
      )}
      <table className="forge-data-table content-breakdown__table forge-typography--body2">
        <thead>
          <tr><th>Part</th><th className="num">Tokens</th><th className="num">Share</th><th className="num">From cache</th>
            <th className="num">Est. cost</th></tr>
        </thead>
        <tbody>
          {parts.map((p) => (
            <tr key={p.part} title={PART_HELP[p.part]}>
              <td><span className="row gap-xsmall"><i className="content-swatch" style={{ background: PART_COLOR[p.part] }} />{p.label}</span></td>
              <td className="num">{count(p.tokens)}</td>
              <td className="num">{pct(p.tokens / Math.max(1, b.inputTokens))}</td>
              <td className="num">{pct(p.cacheRead / Math.max(1, p.tokens))}</td>
              <td className="num">{billed ? money(p.cost) : "–"}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="muted forge-typography--body2">
        An estimate: each part's share of the text, scaled so the parts add up to the {count(b.inputTokens)} tokens recorded. Parts
        read from the provider's prompt cache bill at a fraction of the full rate, which is why the cost bar differs.
      </p>
    </section>
  );
}

function StackBar({ label, parts, value, total, describe }: {
  label: string; parts: PartBreakdown[]; value: (p: PartBreakdown) => number; total: number; describe: (p: PartBreakdown) => string;
}) {
  const [hover, setHover] = useState<PartBreakdown | null>(null);
  return (
    <div className="content-bar">
      <div className="content-bar__label forge-typography--body2">{label}</div>
      <div className="content-bar__track" role="img" aria-label={`${label}: ${parts.map((p) => `${p.label} ${describe(p)}`).join("; ")}`}>
        {parts.map((p) => {
          const w = total > 0 ? (value(p) / total) * 100 : 0;
          if (w <= 0) return null;
          return (
            <span key={p.part} className="content-bar__seg" style={{ flexBasis: `${w}%`, background: PART_COLOR[p.part] }}
              onMouseEnter={() => setHover(p)} onMouseLeave={() => setHover(null)} />
          );
        })}
        {hover && <div className="content-bar__tip forge-typography--body2" aria-hidden="true"><b>{hover.label}</b> · {describe(hover)}</div>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------ the request itself

function ToolsSection({ c }: { c: RequestContent }) {
  if (c.tools.length === 0) return null;
  const total = c.tools.reduce((a, t) => a + t.tokens, 0);
  return (
    <details className="content-group">
      <summary className="forge-typography--body1">
        <i className="content-swatch" style={{ background: PART_COLOR.tools }} />
        <b>Tool definitions</b> · {c.tools.length} tools · about {tokens(total)} tokens{c.firstTurn ? "" : " · resent"}
      </summary>
      <div className="stack gap-xsmall">
        {c.tools.map((t) => (
          <details key={t.index} className="content-item">
            <summary className="forge-typography--body2"><span className="mono">{t.name}</span> · about {tokens(t.tokens)} tokens
              {t.description && <span className="muted"> · {t.description.split("\n")[0]}</span>}</summary>
            <ItemText requestId={c.requestId} kind="tool" index={t.index} text={t.text} truncated={t.truncated} chars={t.chars} />
          </details>
        ))}
      </div>
    </details>
  );
}

function MessagesSection({ c }: { c: RequestContent }) {
  const resent = c.segments.filter((s) => !s.isNew);
  const fresh = c.segments.filter((s) => s.isNew);
  const resentTokens = resent.reduce((a, s) => a + s.tokens, 0);
  return (
    <section className="stack gap-small">
      <h3 className="forge-typography--heading2 card-title">Messages, in order</h3>
      {resent.length > 0 && (
        <details className="content-group">
          <summary className="forge-typography--body1">
            <b>Resent from earlier turns</b> · {resent.length} items · about {tokens(resentTokens)} tokens
          </summary>
          <div className="stack gap-xsmall">{resent.map((s) => <Segment key={s.index} s={s} requestId={c.requestId} />)}</div>
        </details>
      )}
      <div className="stack gap-xsmall">
        {fresh.length > 0 && resent.length > 0 && <div className="muted forge-typography--body2">New this turn</div>}
        {fresh.map((s) => <Segment key={s.index} s={s} requestId={c.requestId} open />)}
      </div>
    </section>
  );
}

function Segment({ s, requestId, open }: { s: ContentSegment; requestId: string; open?: boolean }) {
  return (
    <details className="content-item" open={open || undefined}>
      <summary className="forge-typography--body2">
        <i className="content-swatch" style={{ background: PART_COLOR[s.part] }} />
        <span className="content-role">{s.role}</span> {s.label} · about {tokens(s.tokens)} tokens
        <span className="muted"> · {PART_LABEL[s.part]}</span>
      </summary>
      <ItemText requestId={requestId} kind="segment" index={s.index} text={s.text} truncated={s.truncated} chars={s.chars} />
    </details>
  );
}

const PART_LABEL: Record<ContentPart, string> = {
  tools: "tool definitions", instructions: "instructions", conversation: "earlier conversation",
  toolResults: "tool result", injected: "client-injected", prompt: "this turn's prompt",
};

function ResponseSection({ c }: { c: RequestContent }) {
  const r = c.response;
  return (
    <section className="stack gap-small">
      <h3 className="forge-typography--heading2 card-title">The response</h3>
      {r.thinking !== null && (
        <details className="content-item">
          <summary className="forge-typography--body2">The model's thinking · {count(r.thinkingChars)} characters</summary>
          <ItemText requestId={c.requestId} kind="thinking" index={0} text={r.thinking} truncated={r.thinkingTruncated} chars={r.thinkingChars} />
        </details>
      )}
      {r.text
        ? <ItemText requestId={c.requestId} kind="response" index={0} text={r.text} truncated={r.textTruncated} chars={r.textChars} />
        : r.toolCalls.length === 0 && <p className="muted">No text in the response.</p>}
      {r.toolCalls.map((t, i) => (
        <div key={i} className="stack gap-xsmall">
          <span className="forge-typography--body2">Tool call: <span className="mono">{t.name}</span></span>
          <pre className="content-text">{t.arguments}</pre>
        </div>
      ))}
    </section>
  );
}

/** A preview, with the rest loaded from the server on request. Plain text only. */
function ItemText({ requestId, kind, index, text, truncated, chars }: {
  requestId: string; kind: "segment" | "tool" | "response" | "thinking"; index: number; text: string; truncated: boolean; chars: number;
}) {
  const [full, setFull] = useState<string | null>(null);
  const [state, setState] = useState<"idle" | "loading" | "error">("idle");
  const shown = full ?? text;
  return (
    <div className="stack gap-xsmall">
      <pre className="content-text">{shown || "(empty)"}</pre>
      {truncated && full === null && (
        <div className="row gap-small">
          <span className="muted forge-typography--body2">Showing {count(text.length)} of {count(chars)} characters.</span>
          <forge-button dense variant="outlined" disabled={state === "loading" || undefined} onClick={async () => {
            setState("loading");
            try {
              setFull((await api.contentItem(requestId, kind, index)).text);
              setState("idle");
            } catch {
              setState("error");
            }
          }}>{state === "loading" ? "Loading…" : "Show the rest"}</forge-button>
          {state === "error" && <span className="forge-typography--body2" style={{ color: "var(--forge-theme-error)" }}>Could not load it.</span>}
        </div>
      )}
    </div>
  );
}
